import React, { useEffect, useState, useMemo } from "react";
import { api, formatDate, formatApiErrorDetail } from "../lib/api";
import { EnvelopeSimple, PaperPlaneTilt, Trash, Eye, CheckCircle, XCircle, FileText } from "@phosphor-icons/react";
import { toast } from "sonner";

const TEMPLATES = [
  {
    id: "haccp",
    label: "Questionario HACCP",
    subject: "Questionario HACCP da compilare",
    body:
      "Gentile {cliente},\n\n" +
      "le inviamo il link per la compilazione del questionario HACCP necessario per attivare il servizio richiesto.\n\n" +
      "Le chiediamo gentilmente di compilarlo entro pochi giorni dalla ricezione, in modo da poter procedere con l'attività concordata:\n\n" +
      "[INCOLLA QUI IL LINK AL QUESTIONARIO]\n\n" +
      "In caso di difficoltà siamo a Sua completa disposizione.\n\n" +
      "Cordiali saluti",
  },
  {
    id: "documenti",
    label: "Richiesta documenti",
    subject: "Richiesta documentazione",
    body:
      "Gentile {cliente},\n\n" +
      "per poter procedere con l'attività concordata, le chiediamo cortesemente di farci pervenire la seguente documentazione:\n\n" +
      "- Visura camerale aggiornata\n" +
      "- Documento di identità del legale rappresentante\n" +
      "- [altri documenti richiesti]\n\n" +
      "Può rispondere a questa email allegando i documenti oppure inviarli direttamente al nostro indirizzo PEC.\n\n" +
      "Grazie per la collaborazione.\n\n" +
      "Cordiali saluti",
  },
  {
    id: "generica",
    label: "Comunicazione generica",
    subject: "",
    body:
      "Gentile {cliente},\n\n" +
      "[testo libero]\n\n" +
      "Cordiali saluti",
  },
];

const STATUS_META = {
  sent: { label: "Inviata", cls: "bg-[#008A3C] text-white border-[#008A3C]" },
  failed: { label: "Fallita", cls: "bg-[#D32F2F] text-white border-[#D32F2F]" },
};

export default function Email() {
  const [items, setItems] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCompose, setShowCompose] = useState(false);
  const [previewItem, setPreviewItem] = useState(null);

  async function load() {
    setLoading(true);
    try {
      const [emRes, cliRes] = await Promise.all([
        api.get("/emails"),
        api.get("/clients"),
      ]);
      setItems(emRes.data || []);
      setClients(cliRes.data || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore caricamento");
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); }, []);

  async function del(item) {
    if (!window.confirm(`Eliminare l'email "${item.subject}" dallo storico?`)) return;
    try {
      await api.delete(`/emails/${item.id}`);
      toast.success("Email eliminata dallo storico");
      await load();
    } catch (e) {
      toast.error("Errore eliminazione");
    }
  }

  const stats = useMemo(() => ({
    totali: items.length,
    inviate: items.filter((i) => i.status === "sent").length,
    fallite: items.filter((i) => i.status === "failed").length,
  }), [items]);

  return (
    <div className="p-4 md:p-12 max-w-[1400px] mx-auto">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8 md:mb-10">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#64748B] mb-3">Comunicazioni</div>
          <h1 className="font-display text-3xl sm:text-4xl md:text-5xl font-black tracking-tighter leading-none">Email</h1>
          <p className="text-sm text-[#64748B] mt-3">{stats.totali} totali · {stats.inviate} inviate · {stats.fallite > 0 && <span className="text-[#D32F2F]">{stats.fallite} fallite</span>}</p>
        </div>
        <button
          onClick={() => setShowCompose(true)}
          data-testid="btn-compose-email"
          className="btn-primary px-5 py-3 text-xs uppercase tracking-[0.15em] font-bold rounded-sm flex items-center gap-2 self-start"
        >
          <PaperPlaneTilt size={14} weight="bold" /> Nuova Email
        </button>
      </div>

      {loading ? (
        <div className="text-center py-12 text-[#64748B] text-sm">Caricamento…</div>
      ) : items.length === 0 ? (
        <div className="swiss-card p-12 text-center">
          <EnvelopeSimple size={40} weight="duotone" className="mx-auto text-[#64748B] mb-3" />
          <h3 className="font-display text-xl font-bold mb-2">Nessuna email inviata</h3>
          <p className="text-sm text-[#64748B] mb-4">Componi la prima email per inviare il link del questionario HACCP o altre comunicazioni.</p>
          <button onClick={() => setShowCompose(true)} className="btn-primary px-5 py-3 text-xs uppercase tracking-[0.15em] font-bold rounded-sm inline-flex items-center gap-2">
            <PaperPlaneTilt size={14} weight="bold" /> Componi
          </button>
        </div>
      ) : (
        <div className="swiss-card overflow-hidden">
          {/* Desktop table */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#F4F5F7]">
                <tr className="text-left">
                  <th className="py-3 px-4 text-[10px] uppercase tracking-[0.15em] text-[#64748B] font-bold">Data</th>
                  <th className="py-3 px-4 text-[10px] uppercase tracking-[0.15em] text-[#64748B] font-bold">Destinatario</th>
                  <th className="py-3 px-4 text-[10px] uppercase tracking-[0.15em] text-[#64748B] font-bold">Oggetto</th>
                  <th className="py-3 px-4 text-[10px] uppercase tracking-[0.15em] text-[#64748B] font-bold">Inviata da</th>
                  <th className="py-3 px-4 text-[10px] uppercase tracking-[0.15em] text-[#64748B] font-bold">Stato</th>
                  <th className="py-3 px-4"></th>
                </tr>
              </thead>
              <tbody>
                {items.map((e) => {
                  const meta = STATUS_META[e.status] || STATUS_META.sent;
                  return (
                    <tr key={e.id} className="border-t border-[#E2E8F0] hover:bg-[#F4F5F7]" data-testid={`email-row-${e.id}`}>
                      <td className="py-3 px-4 text-xs text-[#64748B]">{formatDate(e.sent_at)}</td>
                      <td className="py-3 px-4">
                        <div className="font-semibold text-sm">{e.cliente_snapshot?.ragione_sociale || "—"}</div>
                        <div className="text-xs text-[#64748B] font-mono">{e.to_email}</div>
                      </td>
                      <td className="py-3 px-4 max-w-[300px] truncate" title={e.subject}>{e.subject}</td>
                      <td className="py-3 px-4 text-xs text-[#64748B]">{e.sent_by_name || "—"}</td>
                      <td className="py-3 px-4">
                        <span className={`inline-block px-2 py-0.5 text-[9px] uppercase tracking-[0.15em] font-bold border rounded-sm ${meta.cls}`}>
                          {meta.label}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-right">
                        <div className="inline-flex gap-1">
                          <button onClick={() => setPreviewItem(e)} className="p-1.5 text-[#0033A0] hover:bg-blue-50 rounded-sm" title="Anteprima" data-testid={`email-view-${e.id}`}><Eye size={16} /></button>
                          <button onClick={() => del(e)} className="p-1.5 text-[#64748B] hover:text-[#D32F2F]" title="Elimina" data-testid={`email-delete-${e.id}`}><Trash size={16} /></button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="md:hidden divide-y divide-[#E2E8F0]">
            {items.map((e) => {
              const meta = STATUS_META[e.status] || STATUS_META.sent;
              return (
                <div key={e.id} className="px-4 py-4">
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold text-sm truncate">{e.subject}</div>
                      <div className="text-[11px] text-[#64748B] mt-0.5">{e.cliente_snapshot?.ragione_sociale || e.to_email}</div>
                    </div>
                    <span className={`inline-block px-2 py-0.5 text-[9px] uppercase tracking-[0.15em] font-bold border rounded-sm ${meta.cls}`}>{meta.label}</span>
                  </div>
                  <div className="text-[11px] text-[#64748B]">{formatDate(e.sent_at)} · {e.sent_by_name}</div>
                  <div className="flex gap-2 mt-2">
                    <button onClick={() => setPreviewItem(e)} className="text-[10px] uppercase tracking-[0.15em] font-bold text-[#0033A0]">Anteprima</button>
                    <button onClick={() => del(e)} className="text-[10px] uppercase tracking-[0.15em] font-bold text-[#D32F2F] ml-auto">Elimina</button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {showCompose && (
        <ComposeModal
          clients={clients}
          onClose={() => setShowCompose(false)}
          onSent={async () => { setShowCompose(false); await load(); }}
        />
      )}
      {previewItem && <PreviewModal item={previewItem} onClose={() => setPreviewItem(null)} />}
    </div>
  );
}

function ComposeModal({ clients, onClose, onSent }) {
  const [clienteId, setClienteId] = useState("");
  const [toEmail, setToEmail] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  function onClientChange(id) {
    setClienteId(id);
    const c = clients.find((x) => x.id === id);
    if (c?.email) setToEmail(c.email);
  }

  function applyTemplate(tplId) {
    const t = TEMPLATES.find((x) => x.id === tplId);
    if (!t) return;
    const cliente = clients.find((c) => c.id === clienteId);
    const cname = cliente?.ragione_sociale || "Cliente";
    setSubject(t.subject);
    setBody(t.body.replace(/\{cliente\}/g, cname));
    toast.success(`Modello "${t.label}" caricato`);
  }

  async function submit(e) {
    e.preventDefault();
    if (!toEmail.trim()) return toast.error("Inserisci il destinatario");
    if (!subject.trim()) return toast.error("Inserisci l'oggetto");
    if (!body.trim()) return toast.error("Scrivi il messaggio");
    setSending(true);
    try {
      await api.post("/emails", {
        cliente_id: clienteId || null,
        to_email: toEmail.trim(),
        subject: subject.trim(),
        body,
      });
      toast.success("Email inviata");
      onSent();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Errore invio");
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white max-w-2xl w-full max-h-[90vh] overflow-y-auto rounded-md shadow-2xl" onClick={(ev) => ev.stopPropagation()}>
        <div className="p-6 border-b border-[#E2E8F0] flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#0033A0] mb-1">Compositore</div>
            <h3 className="font-display text-2xl font-bold">Nuova Email</h3>
          </div>
        </div>
        <form onSubmit={submit} className="p-4 sm:p-6 space-y-4">
          <Field label="Cliente (opzionale)">
            <select value={clienteId} onChange={(e) => onClientChange(e.target.value)} className="inp" data-testid="email-cliente-select">
              <option value="">— Destinatario libero —</option>
              {clients.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.ragione_sociale} {c.email ? `· ${c.email}` : ""}
                </option>
              ))}
            </select>
          </Field>

          <Field label="Destinatario (email) *">
            <input
              required
              type="email"
              value={toEmail}
              onChange={(e) => setToEmail(e.target.value)}
              placeholder="cliente@esempio.it"
              className="inp"
              data-testid="email-to-input"
            />
          </Field>

          <div>
            <label className="block text-[10px] font-bold uppercase tracking-[0.15em] mb-1.5">Modelli predefiniti</label>
            <div className="flex flex-wrap gap-2">
              {TEMPLATES.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => applyTemplate(t.id)}
                  data-testid={`tpl-${t.id}`}
                  className="px-3 py-2 text-[10px] uppercase tracking-[0.15em] font-bold border border-[#E2E8F0] rounded-sm hover:border-[#0033A0] hover:text-[#0033A0] flex items-center gap-1.5"
                >
                  <FileText size={12} weight="bold" /> {t.label}
                </button>
              ))}
            </div>
          </div>

          <Field label="Oggetto *">
            <input
              required
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Es. Questionario HACCP da compilare"
              className="inp"
              data-testid="email-subject-input"
            />
          </Field>

          <Field label="Messaggio *">
            <textarea
              required
              rows={11}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Scrivi il messaggio. Le URL http(s)://... saranno trasformate in link cliccabili."
              className="inp font-sans text-sm"
              data-testid="email-body-input"
            />
            <span className="text-[10px] text-[#64748B] mt-1 block">
              Doppio a-capo per nuovo paragrafo · Le URL diventano automaticamente cliccabili · Firma e intestazione azienda sono aggiunte in automatico
            </span>
          </Field>

          <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 pt-4 border-t border-[#E2E8F0]">
            <button type="button" onClick={onClose} className="px-4 py-2 text-xs uppercase tracking-[0.15em] font-bold border border-[#E2E8F0] rounded-sm">
              Annulla
            </button>
            <button
              type="submit"
              disabled={sending}
              data-testid="email-send-btn"
              className="btn-primary px-5 py-2 text-xs uppercase tracking-[0.15em] font-bold rounded-sm flex items-center justify-center gap-2"
            >
              <PaperPlaneTilt size={14} weight="bold" /> {sending ? "Invio in corso…" : "Invia Email"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function PreviewModal({ item, onClose }) {
  const [full, setFull] = useState(item);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/emails/${item.id}`);
        setFull(data);
      } catch {}
    })();
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [item.id, onClose]);

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white max-w-2xl w-full max-h-[90vh] overflow-y-auto rounded-md shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="p-6 border-b border-[#E2E8F0]">
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#64748B] mb-1">Email · {formatDate(full.sent_at)}</div>
          <h3 className="font-display text-xl font-bold">{full.subject}</h3>
          <div className="text-xs text-[#64748B] mt-2">
            <strong>A:</strong> {full.to_email} {full.cliente_snapshot?.ragione_sociale && <>· {full.cliente_snapshot.ragione_sociale}</>}
            <br />
            <strong>Da:</strong> {full.sent_by_name}
            {full.status === "failed" && (
              <span className="ml-2 text-[#D32F2F] inline-flex items-center gap-1"><XCircle size={12} weight="fill" /> Invio fallito: {full.error}</span>
            )}
            {full.status === "sent" && (
              <span className="ml-2 text-[#008A3C] inline-flex items-center gap-1"><CheckCircle size={12} weight="fill" /> Inviata correttamente</span>
            )}
          </div>
        </div>
        <div className="p-6 text-sm whitespace-pre-wrap font-sans leading-relaxed text-[#0A0A0A]" data-testid="email-preview-body">
          {full.body || "—"}
        </div>
        <div className="p-4 border-t border-[#E2E8F0] flex justify-end">
          <button onClick={onClose} className="px-4 py-2 text-xs uppercase tracking-[0.15em] font-bold border border-[#E2E8F0] rounded-sm">Chiudi</button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-[10px] font-bold uppercase tracking-[0.15em] mb-1.5">{label}</label>
      {children}
    </div>
  );
}
